import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Lock, Mail } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) navigate("/");
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Erfolgreich angemeldet!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Anmeldung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* Form Side */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 bg-white min-h-[60vh] lg:min-h-screen">
        <div className="w-full max-w-md space-y-6 sm:space-y-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_jobcenterhome/artifacts/7j8vsiwu_ditib_logo_DE_2026_Wohneim.png" 
                alt="DITIB Nürnberg Wohnheim" 
                className="h-20 sm:h-24 max-w-[300px] object-contain"
              />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Wohnheim Verwaltung
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Zimmerverwaltung & Zahlungsverfolgung
            </p>
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="space-y-1 p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>Anmelden</CardTitle>
              <CardDescription className="text-sm">Geben Sie Ihre Zugangsdaten ein</CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Benutzername</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={1.5} />
                    <Input
                      id="email"
                      type="email"
                      placeholder="Benutzername"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Passwort</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={1.5} />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11"
                  style={{ backgroundColor: '#2d3436' }}
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? "Anmeldung..." : "Anmelden"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Image Side - Hidden on mobile */}
      <div className="hidden lg:flex lg:flex-1 items-center justify-center p-8" style={{ backgroundColor: '#2e3436' }}>
        <div className="w-full flex flex-col items-center text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_jobcenterhome/artifacts/q37zu5hl_binaresmi_2026.png"
            alt="Wohnheim"
            className="mb-8 max-h-[500px] object-contain mx-auto"
          />
          <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Professionelle Wohnheimverwaltung
          </h2>
          <p className="text-slate-400">
            Verwalten Sie Ihre Zimmer, Bewohner und Zahlungen effizient.
          </p>
        </div>
      </div>
    </div>
  );
}
